import { useFormik } from 'formik'
import { FC, useEffect, useMemo, useState } from 'react'
import * as Yup from 'yup'
import {
  ButtonType,
  OptionsHelper,
  showAlert,
  showConfirmAlert,
} from '../../../../../_metronic/helpers'
import { isVinculoMedicoAtivo } from 'app/modules/auth/core/vinculos'
import {
  Button,
  FormDateInput,
  FormSelect,
  FormTimeInput,
  IOption,
  InfoAlert,
  LoadingIndicator,
  useModal,
} from '../../../../components'
import { EspecialidadeList } from '../../especialidades/especialidades-list/core/_models'
import { FuncionarioView } from '../../funcionarios/funcionarios-list/core/_models'
import {
  EscalaAgendaViewEmpresa,
  EscalaAgendaViewPessoa,
  TipoAtendimentoEscala,
  TipoEscala,
} from '../core/_models'
import {
  deleteAgendasEmpresa,
  deleteAgendasPessoa,
  getMeusVinculos,
  updateAgendaEmpresa,
  updateAgendaPessoa,
} from '../core/_requests'
import {
  ErrosValidacao,
  MENSAGEM_ESCALA_DUPLICADA,
  TIPO_ESCALA_LABELS,
  ehErroDeEscalaDuplicada,
  extrairErrosValidacao,
  formatHorario,
  mensagensDeFalha,
} from '../core/escalaHelpers'
import { ErrosVinculos } from './ErrosVinculos'
import { EixoEscala } from './EscalaFormModal'

type Props = {
  eixo: EixoEscala
  /** Obrigatório no eixo empresa. */
  empresa?: number
  agenda: EscalaAgendaViewPessoa | EscalaAgendaViewEmpresa
  onSuccess: () => void
}

type ValoresFormulario = {
  tipo: IOption | null
  empresas: IOption[]
  especialidades: IOption[]
  tiposAtendimento: IOption[]
  data: string
  horarioInicio: string
  horarioTermino: string
}

const TIPO_OPTIONS: IOption[] = [
  { value: TipoEscala.Disponibilidade, label: TIPO_ESCALA_LABELS[TipoEscala.Disponibilidade] },
  { value: TipoEscala.Indisponibilidade, label: TIPO_ESCALA_LABELS[TipoEscala.Indisponibilidade] },
]

const ehDisponibilidade = (tipo: IOption | null) => tipo?.value === TipoEscala.Disponibilidade

const schema = Yup.object({
  tipo: Yup.object().nullable().required('Informe o tipo da marcação'),
  data: Yup.string().required('Informe a data'),
  especialidades: Yup.array().when('tipo', {
    is: ehDisponibilidade,
    then: (s) => s.min(1, 'Escolha ao menos uma especialidade'),
    otherwise: (s) => s,
  }),
  tiposAtendimento: Yup.array().when('tipo', {
    is: ehDisponibilidade,
    then: (s) => s.min(1, 'Escolha ao menos um tipo de atendimento'),
    otherwise: (s) => s,
  }),
  horarioInicio: Yup.string().when('tipo', {
    is: ehDisponibilidade,
    then: (s) => s.required('Informe o horário de início'),
    otherwise: (s) => s,
  }),
  horarioTermino: Yup.string()
    .when('tipo', {
      is: ehDisponibilidade,
      then: (s) => s.required('Informe o horário de término'),
      otherwise: (s) => s,
    })
    .test('depois', 'O horário de término deve ser posterior ao de início', function (valor) {
      const inicio = this.parent.horarioInicio
      return !valor || !inicio || valor > inicio
    }),
})

const semRepetirPorValor = (options: IOption[]): IOption[] => {
  const vistos = new Set<IOption['value']>()
  return options.filter((option) => {
    if (vistos.has(option.value)) return false
    vistos.add(option.value)
    return true
  })
}

/**
 * Edita **um dia** da escala (`PUT /escalas/agenda` / `PUT /escalas/empresa/agenda/{empresa}`)
 * e permite excluí-lo (`DELETE .../agenda?ids=`).
 *
 * Não existe `PUT /escalas/{id}` no backend: alterar a escala inteira é
 * excluir + recriar, feito pelo `EscalaFormModal` com confirmação explícita.
 */
export const EscalaAgendaDiaModal: FC<Props> = ({ eixo, empresa, agenda, onSuccess }) => {
  const { closeModal } = useModal()

  const [vinculos, setVinculos] = useState<FuncionarioView[]>([])
  const [erros, setErros] = useState<ErrosValidacao | null>(null)
  const [excluindo, setExcluindo] = useState(false)

  const agendaPessoal = agenda as EscalaAgendaViewPessoa

  useEffect(() => {
    if (eixo !== 'pessoal') return

    let ativo = true
    getMeusVinculos()
      .then((dados) => {
        if (ativo) setVinculos(dados ?? [])
      })
      .catch((error) => setErros(extrairErrosValidacao(error)))

    return () => {
      ativo = false
    }
  }, [eixo])

  const vinculosMedicosAtivos = useMemo(() => vinculos.filter(isVinculoMedicoAtivo), [vinculos])

  const opcoesEmpresas = useMemo(
    () =>
      semRepetirPorValor(
        vinculosMedicosAtivos
          .filter((vinculo) => vinculo.empresa?.id != null)
          .map((vinculo) => ({
            value: vinculo.empresa!.id,
            label: vinculo.empresa!.nomeFantasia ?? vinculo.empresa!.nome ?? '',
          }))
      ),
    [vinculosMedicosAtivos]
  )

  const opcoesEspecialidades = useMemo(
    () =>
      semRepetirPorValor(
        vinculosMedicosAtivos
          .flatMap((vinculo) => vinculo.especialidades ?? [])
          .filter((especialidade): especialidade is EspecialidadeList => !!especialidade)
          .map((especialidade) => ({ value: especialidade.id, label: especialidade.nome }))
      ),
    [vinculosMedicosAtivos]
  )

  const formik = useFormik<ValoresFormulario>({
    initialValues: {
      tipo: TIPO_OPTIONS.find((opcao) => opcao.value === agenda.tipo) ?? TIPO_OPTIONS[0],
      empresas: semRepetirPorValor(
        (agendaPessoal.empresas ?? [])
          .filter((item) => item?.id != null)
          .map((item) => ({ value: item.id, label: item.nomeFantasia ?? item.nome ?? '' }))
      ),
      especialidades: semRepetirPorValor(
        (agenda.vinculos ?? [])
          .map((vinculo) => vinculo.especialidade)
          .filter((especialidade): especialidade is EspecialidadeList => !!especialidade)
          .map((especialidade) => ({ value: especialidade.id, label: especialidade.nome }))
      ),
      tiposAtendimento: (agenda.tiposAtendimento ?? []).map((tipo) => ({
        value: tipo,
        label: tipo,
      })),
      data: agenda.data,
      horarioInicio: formatHorario(agenda.horarioInicio),
      horarioTermino: formatHorario(agenda.horarioTermino),
    },
    validationSchema: schema,
    onSubmit: async (values, { setSubmitting }) => {
      setErros(null)
      setSubmitting(true)

      const corpoComum = {
        id: agenda.id,
        tipo: values.tipo!.value as TipoEscala,
        data: values.data,
        horarioInicio: values.horarioInicio || null,
        horarioTermino: values.horarioTermino || null,
        especialidades: values.especialidades.length
          ? values.especialidades.map((opcao) => Number(opcao.value))
          : null,
        tiposAtendimento: values.tiposAtendimento.length
          ? (values.tiposAtendimento.map((opcao) => opcao.value) as TipoAtendimentoEscala[])
          : null,
      }

      try {
        if (eixo === 'empresa') {
          await updateAgendaEmpresa(empresa!, corpoComum)
        } else {
          await updateAgendaPessoa({
            ...corpoComum,
            empresas: values.empresas.length
              ? values.empresas.map((opcao) => Number(opcao.value))
              : null,
          })
        }
        showAlert({ title: 'Pronto!', text: 'Marcação atualizada.', icon: 'success' })
        onSuccess()
        closeModal()
      } catch (error) {
        setErros(extrairErrosValidacao(error))
      } finally {
        setSubmitting(false)
      }
    },
  })

  const excluirDia = async () => {
    const confirmado = await showConfirmAlert({
      title: 'Excluir esta marcação?',
      text: 'Apenas este dia será removido da escala.',
    })
    if (!confirmado) return

    setExcluindo(true)
    setErros(null)
    try {
      if (eixo === 'empresa') await deleteAgendasEmpresa(empresa!, [agenda.id])
      else await deleteAgendasPessoa([agenda.id])

      showAlert({ title: 'Pronto!', text: 'Marcação removida.', icon: 'success' })
      onSuccess()
      closeModal()
    } catch (error) {
      setErros(extrairErrosValidacao(error))
    } finally {
      setExcluindo(false)
    }
  }

  const disponibilidade = ehDisponibilidade(formik.values.tipo)
  const indisponibilidadeGlobal =
    eixo === 'pessoal' && !disponibilidade && formik.values.empresas.length === 0

  /*
   * Mesma regra do `EscalaFormModal`: sem empresa, `processaVinculos` não lê as
   * especialidades (o `Stream` sobre as empresas nunca roda) e o bloqueio sai
   * total. Em vez de descartar a escolha em silêncio, a tela desabilita o campo
   * e diz que o bloqueio é necessariamente total.
   */
  const { setFieldValue } = formik
  const especialidadesEscolhidas = formik.values.especialidades.length
  useEffect(() => {
    if (indisponibilidadeGlobal && especialidadesEscolhidas) setFieldValue('especialidades', [])
  }, [indisponibilidadeGlobal, especialidadesEscolhidas, setFieldValue])

  const errosDeVinculo = erros?.porCampo.vinculos ?? []
  const exibiuDuplicada = !!erros && ehErroDeEscalaDuplicada(erros)
  // Garante feedback mesmo quando o erro não traz envelope (rede, timeout, CORS).
  const outrosErros = mensagensDeFalha(erros, {
    exibiuVinculos: errosDeVinculo.length > 0,
    exibiuDuplicada,
  })

  return (
    <form noValidate onSubmit={formik.handleSubmit}>
      {exibiuDuplicada && (
        <InfoAlert variant='warning' title='Marcação já cadastrada'>
          {MENSAGEM_ESCALA_DUPLICADA}
        </InfoAlert>
      )}

      <ErrosVinculos
        titulo='Estes vínculos não foram aceitos e a marcação não foi salva:'
        itens={errosDeVinculo}
      />
      <ErrosVinculos titulo='Não foi possível salvar:' itens={outrosErros} />

      <div className='row'>
        <FormSelect
          formik={formik}
          name='tipo'
          label='Tipo de marcação'
          options={TIPO_OPTIONS}
          className='col-md-6 mb-5'
          isRequired
        />
        <FormDateInput
          formik={formik}
          name='data'
          label='Data'
          className='col-md-6 mb-5'
          isRequired
        />
      </div>

      {eixo === 'pessoal' && (
        <FormSelect
          formik={formik}
          name='empresas'
          label='Instituições'
          placeholder='Selecione as instituições'
          options={opcoesEmpresas}
          isMulti
          className='mb-5'
          isRequired={disponibilidade}
          infoText='Numa indisponibilidade, deixar em branco vale para todas as instituições em que você tem vínculo de médico ativo.'
        />
      )}

      {indisponibilidadeGlobal && (
        <InfoAlert variant='info' title='Bloqueio para todas as suas instituições'>
          Sem empresa selecionada, este bloqueio vale para todas as empresas em que você tem vínculo
          de médico ativo e é necessariamente <strong>total</strong>: atinge todas as suas
          especialidades. Para bloquear só uma especialidade, escolha antes a empresa.
        </InfoAlert>
      )}

      <div className='row'>
        <FormSelect
          formik={formik}
          name='especialidades'
          label='Especialidades'
          placeholder={
            indisponibilidadeGlobal
              ? 'Bloqueio total — não se aplica'
              : disponibilidade
              ? 'Selecione as especialidades'
              : 'Opcional — bloqueia todas se vazio'
          }
          options={eixo === 'pessoal' ? opcoesEspecialidades : undefined}
          fetchOptions={eixo === 'pessoal' ? undefined : OptionsHelper.getOptionsEspecialidades}
          isMulti
          className='col-md-6 mb-5'
          isRequired={disponibilidade}
          isDisabled={indisponibilidadeGlobal}
          infoText={
            indisponibilidadeGlobal
              ? 'Sem instituição, o bloqueio vale para todas as suas especialidades — escolher uma não restringiria nada.'
              : undefined
          }
        />
        <FormSelect
          formik={formik}
          name='tiposAtendimento'
          label='Tipos de atendimento'
          placeholder='Selecione os tipos'
          fetchOptions={OptionsHelper.getOptionsTiposAtendimento}
          isMulti
          className='col-md-6 mb-5'
          isRequired={disponibilidade}
        />
      </div>

      <div className='row'>
        <FormTimeInput
          formik={formik}
          name='horarioInicio'
          label='Horário de início'
          className='col-md-6 mb-5'
          isRequired={disponibilidade}
        />
        <FormTimeInput
          formik={formik}
          name='horarioTermino'
          label='Horário de término'
          className='col-md-6 mb-5'
          isRequired={disponibilidade}
        />
      </div>

      <div className='d-flex justify-content-between pt-3'>
        <Button
          className='btn-light-danger'
          onClick={excluirDia}
          disabled={formik.isSubmitting || excluindo}
        >
          {excluindo ? <LoadingIndicator /> : 'Excluir este dia'}
        </Button>
        <div className='d-flex'>
          <Button
            className='btn-secondary me-2'
            type={ButtonType.Reset}
            onClick={closeModal}
            disabled={formik.isSubmitting || excluindo}
          >
            Cancelar
          </Button>
          <Button
            className='btn-primary'
            type={ButtonType.Submit}
            disabled={formik.isSubmitting || excluindo}
          >
            {formik.isSubmitting ? <LoadingIndicator /> : 'Salvar alterações'}
          </Button>
        </div>
      </div>
    </form>
  )
}
